import { useEffect } from 'react';
import { i18n } from './i18n';

const LANGS = ['fr', 'en', 'de', 'es', 'it'];
const langUrl = (base, lang) => (lang === 'fr' ? base : `${base}${base.includes('?') ? '&' : '?'}lang=${lang}`);

const setAlternates = (base, available = LANGS) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
  [...LANGS.filter(l => l === 'fr' || available.includes(l)), 'x-default'].forEach(lang => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', lang);
    el.setAttribute('href', langUrl(base, lang === 'x-default' ? 'fr' : lang));
    document.head.appendChild(el);
  });
};

const SITE = 'OTOPIECES.COM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, val] = selector.replace(/^meta\[|\]$/g, '').replace(/"/g, '').split('=');
    el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const absolute = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

export const usePageMeta = ({ title, description, image, path, type = 'website', noindex = false, keywords, rawTitle, alternates } = {}) => {
  const kwKey = (keywords || []).join(', ');
  const altKey = (alternates || []).join(',');
  const lang = i18n.get();
  useEffect(() => {
    const fullTitle = rawTitle || (title ? `${title} | ${SITE}` : `${i18n.t('seo.defaultTitle')} | ${SITE}`);
    const raw = (description || i18n.t('seo.defaultDesc')).replace(/\s+/g, ' ').trim();
    const desc = raw.length > 160 ? `${raw.slice(0, 157).replace(/\s+\S*$/, '')}…` : raw;
    const base = `${window.location.origin}${path || window.location.pathname}`;
    const available = alternates ? ['fr', ...alternates] : LANGS;
    const translated = lang === 'fr' || available.includes(lang);
    const url = translated ? langUrl(base, lang) : base;
    const img = absolute(image) || `${BACKEND_URL}/api/branding/og-image`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1');
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', img);
    setMeta('meta[property="og:type"]', 'content', type);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    setMeta('meta[name="twitter:image"]', 'content', img);
    setCanonical(url);
    if (noindex) document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove()); else setAlternates(base, available);
    const kw = document.head.querySelector('meta[name="keywords"]');
    if (kwKey) setMeta('meta[name="keywords"]', 'content', kwKey);
    else if (kw) kw.remove();
  }, [title, description, image, path, type, noindex, kwKey, rawTitle, lang, altKey]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useJsonLd = (id, data) => {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    const scriptId = `jsonld-${id}`;
    let el = document.getElementById(scriptId);
    if (!json) {
      if (el) el.remove();
      return undefined;
    }
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = json;
    return () => { const cur = document.getElementById(scriptId); if (cur) cur.remove(); };
  }, [id, json]);
};
